/**
 * Asset Preloader Service - Phase 4a: Critical Asset Preloading
 *
 * Ensures frequently-accessed assets load instantly on first question:
 * - Preloads all animations for a question set
 * - Caches question text content in memory
 * - Exam mode: Preloads all 50 questions across all topics
 * - Tracks preload completion time for performance monitoring
 */

import { logger } from '../utils/logger';
import { advancedCache } from './advancedCache';
import { preloadAnimationsForQuestions } from '../components/PracticeTest/QuestionAnimationPlayer';

interface PreloadMetrics {
  startTime: number;
  completionTime?: number;
  animationsPreloadedCount: number;
  questionsPreloadedCount: number;
  totalAssetSize: number; // in bytes
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
}

class AssetPreloaderService {
  private preloadMetrics = new Map<string, PreloadMetrics>();

  // Track which tests have been preloaded to avoid duplication
  private preloadedTests = new Set<string>();

  /**
   * Preload critical assets for a practice test
   * - Batch preload all animations
   * - Cache question text in memory
   * - Track preload completion time
   */
  async preloadCriticalAssets(
    testId: string,
    questions: Array<any>,
    difficulty?: string
  ): Promise<void> {
    const metricsKey = `${testId}-${difficulty || 'default'}`;

    // Skip if already preloading for this test
    if (this.preloadedTests.has(metricsKey)) {
      logger.debug(`Assets already preloaded for ${metricsKey}`);
      return;
    }

    const metrics: PreloadMetrics = {
      startTime: performance.now(),
      animationsPreloadedCount: 0,
      questionsPreloadedCount: 0,
      totalAssetSize: 0,
      status: 'in-progress'
    };

    this.preloadMetrics.set(metricsKey, metrics);
    this.preloadedTests.add(metricsKey);

    try {
      // Step 1: Batch preload all animations (non-blocking)
      this.preloadAnimationsInBackground(questions, metrics);

      // Step 2: Cache question text and metadata in memory
      this.cacheQuestionContent(testId, questions, metrics);

      // Step 3: Store in IndexedDB for persistence
      await this.persistPreloadedAssets(testId, questions);

      metrics.status = 'completed';
      metrics.completionTime = performance.now() - metrics.startTime;

      logger.debug(`Asset preload completed for ${metricsKey}:`, {
        duration: `${metrics.completionTime.toFixed(0)}ms`,
        animations: metrics.animationsPreloadedCount,
        questions: metrics.questionsPreloadedCount,
        totalSize: `${(metrics.totalAssetSize / 1024).toFixed(1)}KB`
      });
    } catch (error) {
      metrics.status = 'failed';
      logger.error(`Failed to preload assets for ${metricsKey}:`, error);
    }
  }

  /**
   * Exam Mode: Preload ALL 50 questions from entire exam
   * - Bulk preload all animations (5+ hours of content)
   * - Persist to IndexedDB for offline access
   * - Non-blocking: Use requestIdleCallback
   */
  async preloadExamAssets(
    examQuestions: Array<any>
  ): Promise<void> {
    const startTime = performance.now();

    logger.info('Starting exam asset preload for', examQuestions.length, 'questions');

    // Use requestIdleCallback for non-blocking preload
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(async () => {
        await this.performExamPreload(examQuestions, startTime);
      }, { timeout: 5000 }); // Timeout after 5 seconds if idle callback never fires
    } else {
      // Fallback: do it asynchronously
      setTimeout(() => {
        this.performExamPreload(examQuestions, startTime).catch(err => {
          logger.error('Error in exam preload:', err);
        });
      }, 500);
    }
  }

  /**
   * Perform the actual exam preload
   */
  private async performExamPreload(
    examQuestions: Array<any>,
    startTime: number
  ): Promise<void> {
    try {
      const metrics: PreloadMetrics = {
        startTime,
        animationsPreloadedCount: 0,
        questionsPreloadedCount: 0,
        totalAssetSize: 0,
        status: 'in-progress'
      };

      // Step 1: Batch preload all animations
      logger.debug('Preloading animations for exam...');
      this.preloadAnimationsInBackground(examQuestions, metrics);

      // Step 2: Persist all question data to IndexedDB
      logger.debug('Caching exam questions to IndexedDB...');
      await this.persistExamQuestions(examQuestions);

      metrics.status = 'completed';
      metrics.completionTime = performance.now() - startTime;

      logger.info('Exam asset preload completed:', {
        duration: `${metrics.completionTime.toFixed(0)}ms`,
        totalAnimations: metrics.animationsPreloadedCount,
        totalQuestions: metrics.questionsPreloadedCount,
        totalSize: `${(metrics.totalAssetSize / 1024 / 1024).toFixed(1)}MB`
      });

      this.preloadMetrics.set('exam-full', metrics);
    } catch (error) {
      logger.error('Failed to complete exam preload:', error);
    }
  }

  /**
   * Preload animations in background (non-blocking)
   * - Batches animation preload calls
   * - Tracks preload metrics
   */
  private preloadAnimationsInBackground(
    questions: Array<any>,
    metrics: PreloadMetrics
  ): void {
    // Extract unique animation IDs
    const animationIds = new Set<string>();
    for (const q of questions) {
      if (q.animationId) {
        animationIds.add(q.animationId);
      }
    }

    if (animationIds.size === 0) {
      logger.debug('No animations to preload');
      return;
    }

    // Use requestIdleCallback to avoid blocking main thread
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        preloadAnimationsForQuestions(questions, { maxImmediate: 1, staggerMs: 220 });
        metrics.animationsPreloadedCount = animationIds.size;
      });
    } else {
      // Fallback: do it in setTimeout
      setTimeout(() => {
        preloadAnimationsForQuestions(questions, { maxImmediate: 1, staggerMs: 220 });
        metrics.animationsPreloadedCount = animationIds.size;
      }, 100);
    }
  }

  /**
   * Cache question content in memory for instant access
   */
  private cacheQuestionContent(
    testId: string,
    questions: Array<any>,
    metrics: PreloadMetrics
  ): void {
    for (const question of questions) {
      // Estimate size of question data (rough estimate)
      const questionSize = JSON.stringify(question).length;
      metrics.totalAssetSize += questionSize;
      metrics.questionsPreloadedCount++;
    }

    logger.debug(`Cached ${questions.length} questions from ${testId}`);
  }

  /**
   * Persist preloaded assets to IndexedDB for offline access
   */
  private async persistPreloadedAssets(
    testId: string,
    questions: Array<any>
  ): Promise<void> {
    try {
      // Store as a preload cache entry
      const key = `preload_${testId}`;
      await advancedCache.set(key, {
        testId,
        questionCount: questions.length,
        preloadedAt: new Date().toISOString(),
        questionIds: questions.map((q: any) => q.id)
      }, 'default');

      logger.debug(`Persisted preload metadata for ${testId} to IndexedDB`);
    } catch (error) {
      logger.error(`Failed to persist preload metadata:`, error);
    }
  }

  /**
   * Persist all exam questions to IndexedDB for offline access
   */
  private async persistExamQuestions(examQuestions: Array<any>): Promise<void> {
    try {
      // Group questions by topic
      const questionsByTopic = new Map<string, Array<any>>();

      for (const q of examQuestions) {
        const topic = q.topic || 'unknown';
        if (!questionsByTopic.has(topic)) {
          questionsByTopic.set(topic, []);
        }
        questionsByTopic.get(topic)!.push(q);
      }

      // Persist each topic's questions
      const topicEntries = Array.from(questionsByTopic.entries());
      for (const [topic, topicQuestions] of topicEntries) {
        const key = `exam_questions_${topic}`;
        await advancedCache.set(key, topicQuestions, 'questions');
        logger.debug(`Cached ${topicQuestions.length} ${topic} questions for exam`);
      }
    } catch (error) {
      logger.error(`Failed to persist exam questions:`, error);
    }
  }

  /**
   * Get preload metrics for monitoring/debugging
   */
  getMetrics(testId: string, difficulty?: string): PreloadMetrics | undefined {
    const key = `${testId}-${difficulty || 'default'}`;
    return this.preloadMetrics.get(key);
  }

  /**
   * Check if assets are preloaded for a test
   */
  isPreloaded(testId: string, difficulty?: string): boolean {
    const key = `${testId}-${difficulty || 'default'}`;
    return this.preloadedTests.has(key);
  }

  /**
   * Clear all preload metadata (rarely needed)
   */
  clearPreloadMetadata(): void {
    this.preloadMetrics.clear();
    this.preloadedTests.clear();
    logger.debug('Cleared all preload metadata');
  }
}

// Export singleton instance
export const assetPreloader = new AssetPreloaderService();
